<template>
  <MModal
    :show="show"
    :title="errorModalTitle"
    :type="modalVariant"
    :isFullscreen="isErrorViewScreen"
  >
    <template #content>
      <div class="error-message">
        <template v-if="errorMsg?.message">
          <template v-if="Array.isArray(messageLines)">
            <p v-for="(line, index) in messageLines" :key="index">{{ line }}</p>
          </template>
          <p v-else>{{ errorMsg.message }}</p>
        </template>
        <template v-else>
          <p>Something went wrong when trying to initialise the screener</p>
          <p>Please contact the support team for assistance</p>
        </template>

        <a
          v-if="!isErrorViewScreen && errorMsg.helpLink"
          :href="errorMsg.helpLink"
          target="_blank"
          class="help-link"
          rel="noopener noreferrer"
        >
          <strong>{{ errorMsg.helpText || 'Click here for help' }}</strong>
        </a>
      </div>
    </template>

    <template #footer>
      <MButton
        v-if="showButton"
        class="action-button"
        :variant="modalVariant"
        size="md"
        @click="handleClick"
        :disabled="loading"
      >
        <font-awesome-icon
          v-if="isErrorViewScreen && loading"
          :icon="faSpinner"
          :spin="true"
          class="fast-spin spinner"
        />
        <span v-else>{{ buttonLabel() }}</span>
      </MButton>
    </template>
  </MModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
// Types
import type { PropType } from 'vue'
import type { ErrorMessage } from '@/customTypes'

// Icons
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

// Components
import MButton from '@/components/MButton.vue'
import MModal from '@/components/MModal.vue'

// Services
import FullScreen from '@/services/FullScreen.js'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  errorMsg: {
    type: Object as PropType<ErrorMessage>,
    default: () => ({ message: '', type: 'danger' })
  },
  title: {
    type: String,
    default: ''
  },
  regenerateScreenerId: {
    type: Boolean,
    default: false
  },
  persistent: {
    type: Boolean,
    default: false
  },
  isErrorViewScreen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (e: 'regenerate'): void
  (e: 'retry'): void
  (e: 'close'): void
  (e: 're-enter-code'): void
}>()

const loading = ref(false)

const errorModalTitle = computed(() => {
  return props.title || 'Oops!...'
})

const messageLines = computed(() => {
  if (!props.errorMsg?.message) return []
  const splitMessage = props.errorMsg.message.split('\n')
  return props.regenerateScreenerId ? splitMessage.slice(0, 1) : splitMessage
})

const modalVariant = computed(() => {
  return props.errorMsg?.type === 'warning' ? 'warning' : 'danger'
})

const showButton = computed(() => {
  if (props.persistent) return false

  if (props.isErrorViewScreen) {
    return codeRelatedError.value || requiresRefresh.value
  }

  return showRegenerateButton.value || showRetryButton.value || requiresRefresh.value
})

const codeRelatedError = computed(() => props.errorMsg?.message?.toLowerCase().includes('code'))

const requiresRefresh = computed(
  () =>
    props.errorMsg?.message?.toLowerCase().includes('refresh') ||
    props.errorMsg?.message?.toLowerCase().includes('reload')
)

const showRegenerateButton = computed(
  () => props.regenerateScreenerId && props.errorMsg?.message?.toLowerCase().includes('expired')
)

const showRetryButton = computed(
  () => props.errorMsg?.message?.toLowerCase().includes('code') && !showRegenerateButton.value
)

// Methods
const buttonLabel = () => {
  if (props.isErrorViewScreen) {
    if (codeRelatedError.value) return 'Try Again'
    if (requiresRefresh.value) {
      return props.errorMsg?.message?.toLowerCase().includes('reload') ? 'Reload' : 'Refresh'
    }
    return ''
  }

  if (showRegenerateButton.value) return 'Regenerate Code'
  if (showRetryButton.value) return 'Try Again'
  if (requiresRefresh.value) {
    return props.errorMsg?.message?.toLowerCase().includes('reload') ? 'Reload' : 'Refresh'
  }
  return ''
}

const handleClick = () => {
  if (props.isErrorViewScreen) {
    loading.value = true
    if (codeRelatedError.value) {
      emit('re-enter-code')
    } else if (requiresRefresh.value) {
      handleRefresh()
    }
  } else {
    if (showRegenerateButton.value) emit('regenerate')
    else if (showRetryButton.value) emit('retry')
    else if (requiresRefresh.value) handleRefresh()
  }
}

const handleRefresh = () => {
  FullScreen.cancel()
  window.location.reload()
}
</script>

<style lang="scss" scoped>
.alert-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  &.warning {
    background-color: rgba($warning, 0.2);
    .warning-triangle {
      color: $warning;
    }
  }

  &.danger {
    background-color: rgba($danger, 0.2);
    .danger-triangle {
      color: $danger;
    }
  }
}

.error-message {
  color: $dark;
  margin: 1.5rem 0;
  line-height: 1.5;
  font-weight: 500;

  p {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .help-link {
    display: block;
    margin-top: 1.5rem;
    color: $primary;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>
